<script>
import GlobalConfig from '@/model/global/GlobalSettings'
import {
  changeShopManagerPassword,
  getShopManagerPasswordStatus
} from '@/model/dataLayer/api/MembershipSystem/ShopManagerPassword'
import { showErrorMessage, showSuccessMessage } from '@/model/utils/utils'
import dayjs from 'dayjs'

const MIN_LENGTH = 6
const MAX_LENGTH = 32

export default {
  name: 'ShopManagerPassword',
  data () {
    return {
      loading: false,
      saving: false,
      status: null,
      loadError: '',
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
      showPassword: false,
      rules: {
        length: v => (v?.length >= MIN_LENGTH && v?.length <= MAX_LENGTH) || `长度需为 ${MIN_LENGTH}-${MAX_LENGTH} 位`,
        noOuterSpace: v => !v || v === v.trim() || '首尾不能有空格',
        same: v => v === this.newPassword || '两次输入的新密码不一致'
      }
    }
  },
  computed: {
    shopBlId () {
      return GlobalConfig.blId
    },
    isSet () {
      return !!this.status?.isSet
    },
    canSave () {
      return this.rules.length(this.newPassword) === true &&
        this.rules.noOuterSpace(this.newPassword) === true &&
        this.confirmPassword === this.newPassword &&
        (!this.isSet || this.oldPassword.length > 0)
    }
  },
  async mounted () {
    await this.load()
  },
  methods: {
    formatTime (time) {
      return time ? dayjs(time).format('YYYY-MM-DD HH:mm') : ''
    },
    async load () {
      if (!this.shopBlId) {
        this.loadError = '未找到当前门店，请重新进入门店后再试'
        return
      }
      this.loading = true
      this.loadError = ''
      try {
        this.status = await getShopManagerPasswordStatus(this.shopBlId)
      } catch (e) {
        this.loadError = e.message
      } finally {
        this.loading = false
      }
    },
    async save () {
      if (!this.canSave) return
      this.saving = true
      try {
        this.status = await changeShopManagerPassword(this.shopBlId, this.newPassword, this.oldPassword)
        this.oldPassword = ''
        this.newPassword = ''
        this.confirmPassword = ''
        this.$refs.form?.resetValidation()
        showSuccessMessage('店长密码已保存')
      } catch (e) {
        showErrorMessage(e.message)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<template>
  <div style="max-width: 560px">
    <div class="text-h5 font-weight-bold mb-2">
      门店店长密码
    </div>
    <div class="text-body-2 grey--text text--darken-1 mb-6">
      店长在收银机上手动调整会员集点（纸质卡迁入、补点、赠送、纠错）时需要输入这个密码。
      每家门店一个密码，请只告诉店长本人。连续输错 5 次会锁定 15 分钟。
    </div>

    <v-card
      outlined
      class="pa-6"
      :loading="loading"
    >
      <v-alert
        v-if="loadError"
        type="error"
        text
        dense
      >
        {{ loadError }}
      </v-alert>

      <template v-else-if="status">
        <div class="d-flex align-center mb-6">
          <v-chip
            :color="isSet ? 'success' : 'warning'"
            small
            label
          >
            {{ isSet ? '已设置' : '未设置' }}
          </v-chip>
          <span
            v-if="isSet"
            class="ml-3 text-body-2 grey--text text--darken-1"
          >
            最近更新：{{ formatTime(status.passwordUpdatedAt) }}
            <template v-if="status.updatedByName">（{{ status.updatedByName }}）</template>
          </span>
          <span
            v-else
            class="ml-3 text-body-2"
          >
            未设置前，收银机无法手动调整集点
          </span>
        </div>

        <v-alert
          v-if="status.lockedUntil"
          type="warning"
          text
          dense
        >
          密码输错次数过多，收银机已锁定至 {{ formatTime(status.lockedUntil) }}
        </v-alert>

        <v-form
          ref="form"
          @submit.prevent="save"
        >
          <v-text-field
            v-if="isSet"
            v-model="oldPassword"
            label="旧密码"
            outlined
            dense
            autocomplete="current-password"
            :type="showPassword ? 'text' : 'password'"
            hint="忘记旧密码请联系 Aaden 重置"
            persistent-hint
            class="mb-3"
          />
          <v-text-field
            v-model="newPassword"
            :label="isSet ? '新密码' : '设置密码'"
            outlined
            dense
            autocomplete="new-password"
            :type="showPassword ? 'text' : 'password'"
            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
            :rules="[rules.length, rules.noOuterSpace]"
            counter="32"
            @click:append="showPassword = !showPassword"
          />
          <v-text-field
            v-model="confirmPassword"
            label="再次输入"
            outlined
            dense
            autocomplete="new-password"
            :type="showPassword ? 'text' : 'password'"
            :rules="[rules.same]"
          />
          <div class="d-flex justify-end mt-2">
            <v-btn
              color="primary"
              depressed
              type="submit"
              :loading="saving"
              :disabled="!canSave"
            >
              {{ isSet ? '修改密码' : '保存密码' }}
            </v-btn>
          </div>
        </v-form>
      </template>
    </v-card>
  </div>
</template>
